import { post } from '@si/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { queryKeys } from './queryKeys';

interface Params {
  projectId: string;
  installationId: string;
  taskId: string;
  productKey: string;
}

export function useActivateDevice({
  skipInvalidate = false,
  delayInvalidate = 0,
  onSuccess,
  onError,
}: {
  skipInvalidate?: boolean;
  delayInvalidate?: number;
  onSuccess?: () => void;
  onError?: () => void;
} = {}) {
  const queryClient = useQueryClient();

  const refreshQueries = useCallback(
    (projectId: string, installationId: string) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.getInstallationTasks(projectId, installationId),
      });
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => {
          if (!Array.isArray(queryKey) || queryKey[0] !== 'sensors') {
            return false;
          }

          const queryParams = queryKey[1] as { projectId?: string } | undefined;
          return queryParams?.projectId === projectId;
        },
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.getGateways(projectId, installationId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.getGateways(projectId, 'installationId'),
      });
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => {
          if (!Array.isArray(queryKey) || queryKey[0] !== 'project') {
            return false;
          }

          const queryParams = queryKey[1] as { projectId?: string } | undefined;
          return queryParams?.projectId === projectId;
        },
      });
    },
    [queryClient]
  );

  const { mutate, error, isPending, isSuccess, reset } = useMutation({
    mutationFn: (params: Params) =>
      post({
        url: `/projects/${params.projectId}/installation-tasks/${params.taskId}/activate`,
        body: params,
      }),
    onSuccess: (_, { projectId, installationId }) => {
      if (!skipInvalidate) {
        if (delayInvalidate > 0) {
          setTimeout(() => {
            refreshQueries(projectId, installationId);
          }, delayInvalidate);
        } else {
          refreshQueries(projectId, installationId);
        }
      }
      onSuccess?.();
    },
    onError: () => {
      onError?.();
    },
  });

  const activateDevice = useCallback((params: Params) => mutate(params), [mutate]);

  return {
    activateDevice,
    refreshQueries,
    error,
    isPending,
    isSuccess,
    reset,
  };
}
